import { CircleCheck, Info, Lightbulb, TriangleAlert, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

import {
  noticeVariants,
  noticeIconVariants,
  noticeContentVariants,
  noticeTitleVariants,
  noticeDescriptionVariants,
  noticeLinkVariants,
  noticeLinkIndicatorVariants,
  noticeActionsVariants,
  noticeCloseVariants,
} from './Notice.styles';

import type { NoticeKind, NoticeProps } from './Notice.types';

const defaultIcons: Record<NoticeKind, React.ReactNode> = {
  brand: <Lightbulb />,
  info: <Info />,
  success: <CircleCheck />,
  warning: <TriangleAlert />,
  danger: <TriangleAlert />,
};

/**
 * Notice Component
 *
 * Notification card with a coloured left stripe, status icon, title and message.
 * Used inline and as the body of toasts (see `@/components/primitives/Toast`).
 *
 * @example
 * ```tsx
 * <Notice kind="success" title="Saved" description="Your changes have been saved." />
 * <Notice kind="info" description="Use the selector above to switch layers." />
 * <Notice kind="danger" title="Error" description="An error occurred." dismissible />
 * ```
 */
const Notice = React.forwardRef<HTMLDivElement, NoticeProps>(
  (
    {
      className,
      kind = 'brand',
      scale = 'm',
      title,
      description,
      icon,
      showIcon = true,
      link,
      onLinkClick,
      actions,
      dismissible = false,
      onClose,
      children,
      ...props
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = React.useState(true);

    const handleClose = React.useCallback(() => {
      if (onClose) onClose();
      else setIsVisible(false);
    }, [onClose]);

    if (!isVisible) {
      return null;
    }

    const resolvedKind = kind ?? 'brand';

    return (
      <div
        ref={ref}
        className={cn(noticeVariants({ kind, scale }), className)}
        role="status"
        aria-live="polite"
        {...props}
      >
        {showIcon && (
          <div className={noticeIconVariants({ kind, scale })} aria-hidden="true">
            {icon ?? defaultIcons[resolvedKind]}
          </div>
        )}

        <div className={noticeContentVariants({ scale })}>
          {title && <div className={noticeTitleVariants({ scale })}>{title}</div>}
          {description && <div className={noticeDescriptionVariants({ scale })}>{description}</div>}
          {link &&
            (onLinkClick ? (
              <button type="button" className={noticeLinkVariants({ scale })} onClick={onLinkClick}>
                {link}
                <span className={noticeLinkIndicatorVariants()} aria-hidden="true" />
              </button>
            ) : (
              <span className={noticeLinkVariants({ scale })}>
                {link}
                <span className={noticeLinkIndicatorVariants()} aria-hidden="true" />
              </span>
            ))}
          {children}
        </div>

        {actions && <div className={noticeActionsVariants({ scale })}>{actions}</div>}

        {dismissible && (
          <button
            type="button"
            className={noticeCloseVariants({ scale })}
            onClick={handleClose}
            aria-label="Close notice"
          >
            <X />
          </button>
        )}
      </div>
    );
  }
);

Notice.displayName = 'Notice';

export { Notice };
